// Wipe all client-side state belonging to the previous session.
//
// Why: QA reported account-switching issues — login as user A, then click a
// magic link for user B, and user B inherits user A's UW journey, customer
// record, and cached API responses. Those artefacts live in the persisted
// Zustand stores (`ie-hub-store`, `uw-journey-storage`), the `customerData`
// localStorage key, the Service Worker API cache, and the IndexedDB offline
// queue — none of which the existing per-flow cleanup touched.
//
// Call `hardClearClientState('login')` from every login entry point before
// the new tokens are written, and `hardClearClientState('logout')` from the
// logout finally block. The function is best-effort: every step is wrapped
// so a failure in one (e.g. blocked IndexedDB delete) cannot prevent the
// rest from running.

import { useUWStore } from '../state/uwStore';
import { OnboardingService } from '../api/services/onboardingService';

const PER_SESSION_LOCAL_STORAGE_KEYS = [
  // Auth tokens & user
  'authToken',
  'refreshToken',
  'userData',
  'authRefreshLock',
  // Zustand persist roots
  'ie-hub-store',
  'uw-journey-storage',
  // Customer / onboarding caches
  'customerData',
  'pendingLcode',
  'pendingLcode_temp',
  'qsParam',
  'budget-onboarding-progress',
  // Dashboard / page caches
  'dashboard-state',
  'alreadyShared',
  'companyList',
  'submitDate',
  'totalDocumentsLength',
];

const SW_API_CACHE = 'ie-hub-api-v1';
const OFFLINE_QUEUE_DB = 'ie-hub-offline-queue';

const deleteIndexedDB = (name: string): Promise<void> =>
  new Promise((resolve) => {
    try {
      const req = indexedDB.deleteDatabase(name);
      const done = (): void => resolve();
      req.onsuccess = done;
      req.onerror = done;
      req.onblocked = done;
    } catch {
      resolve();
    }
  });

export type HardClearReason = 'login' | 'logout';

export const hardClearClientState = async (
  _reason: HardClearReason
): Promise<void> => {
  // localStorage — known per-session keys only. We avoid `localStorage.clear()`
  // because preferences such as `notificationsPrePromptDismissed` are not
  // user-specific and clearing them would re-prompt every user on every login.
  for (const key of PER_SESSION_LOCAL_STORAGE_KEYS) {
    try { localStorage.removeItem(key); } catch {}
  }

  // sessionStorage holds only transient OAuth dedupe / state — safe to wipe.
  try { sessionStorage.clear(); } catch {}

  // Reset the in-memory UW journey store so any mounted component does not
  // keep rendering the previous user's data until the next hydration.
  try { useUWStore.getState().resetStore(); } catch {}

  // Per-userId customer record cached inside OnboardingService.
  try { OnboardingService.clearCustomerCache(); } catch {}

  // Service Worker API response cache. Keep static asset caches so the app
  // still boots offline; only evict cached API responses.
  try {
    if (typeof caches !== 'undefined') {
      await caches.delete(SW_API_CACHE);
    }
  } catch {}

  // IndexedDB offline queue — queued submissions belong to the previous user.
  try { await deleteIndexedDB(OFFLINE_QUEUE_DB); } catch {}
};
